/**
 * Maaş ödeme modal'ı — Nakit/IBAN/Karma seçimi.
 */

import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Banknote, Building, GitMerge, CheckCircle2 } from 'lucide-react'
import { odemeKaydet, type OdemeTipi } from '@/lib/services/maas-odeme-service'
import { useAuth } from '@/contexts/AuthContext'
import type { KullaniciPersonel } from '@/types'

interface Props {
  acik: boolean
  personel: KullaniciPersonel | null
  donem: string
  netTutar: number
  onKapat: () => void
  onKaydedildi: () => void
}

export function OdemeModal({ acik, personel, donem, netTutar, onKapat, onKaydedildi }: Props) {
  const { firebaseUser } = useAuth()
  const [tip, setTip] = useState<OdemeTipi>('nakit')
  const [nakit, setNakit] = useState<string>('')
  const [iban, setIban] = useState<string>('')
  const [notlar, setNotlar] = useState<string>('')
  const [hata, setHata] = useState<string | null>(null)
  const [kaydediliyor, setKaydediliyor] = useState(false)

  useEffect(() => {
    if (!acik) return
    setTip('nakit')
    setNakit('')
    setIban('')
    setNotlar('')
    setHata(null)
  }, [acik])

  if (!personel) return null

  async function kaydet(ev: React.FormEvent) {
    ev.preventDefault()
    if (!firebaseUser) return

    setHata(null)

    let nakitTutar: number | undefined
    let ibanTutar: number | undefined

    if (tip === 'nakit') {
      nakitTutar = netTutar
    } else if (tip === 'iban') {
      ibanTutar = netTutar
    } else {
      const n = parseFloat(nakit) || 0
      const i = parseFloat(iban) || 0
      if (n <= 0 || i <= 0) {
        setHata('Karma ödemede hem nakit hem IBAN tutarı pozitif olmalı.')
        return
      }
      if (Math.abs(n + i - netTutar) > 0.01) {
        setHata(`Nakit + IBAN = ${(n + i).toFixed(2)} ₺, net tutar ${netTutar.toFixed(2)} ₺.`)
        return
      }
      nakitTutar = n
      ibanTutar = i
    }

    setKaydediliyor(true)
    try {
      await odemeKaydet(
        {
          personelId: personel!.id,
          donem,
          netTutar,
          odemeTipi: tip,
          nakitTutar,
          ibanTutar,
          notlar,
        },
        firebaseUser.uid
      )
      onKaydedildi()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  return (
    <Modal acik={acik} onKapat={onKapat} baslik="Ödeme Kaydet" boyut="md">
      <form onSubmit={kaydet} className="space-y-4">
        <div className="bg-brand-50 border border-brand-200 rounded-xl p-3">
          <p className="font-semibold text-brand-900">{personel.isim}</p>
          <p className="text-xs text-brand-700">{donem}</p>
          <p className="text-3xl font-bold text-brand-900 tabular-nums mt-2">
            {netTutar.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ₺
          </p>
          <p className="text-xs text-brand-700">net ödenecek</p>
        </div>

        {/* Tip seçici */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ödeme Tipi
          </label>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setTip('nakit')}
              className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors ${
                tip === 'nakit'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <Banknote className="w-5 h-5" />
              <span className="text-xs font-semibold">Nakit</span>
            </button>
            <button
              type="button"
              onClick={() => setTip('iban')}
              className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors ${
                tip === 'iban'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <Building className="w-5 h-5" />
              <span className="text-xs font-semibold">IBAN</span>
            </button>
            <button
              type="button"
              onClick={() => setTip('karma')}
              className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors ${
                tip === 'karma'
                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <GitMerge className="w-5 h-5" />
              <span className="text-xs font-semibold">Karma</span>
            </button>
          </div>
        </div>

        {tip === 'karma' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Nakit (₺)
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={nakit}
                onChange={(e) => setNakit(e.target.value)}
                className="input-base"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                IBAN (₺)
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={iban}
                onChange={(e) => setIban(e.target.value)}
                className="input-base"
                placeholder="0"
              />
            </div>
            {nakit && iban && (
              <div className="col-span-2 text-xs text-center text-gray-600 bg-gray-50 rounded-lg p-2">
                Toplam: {((parseFloat(nakit) || 0) + (parseFloat(iban) || 0)).toLocaleString('de-DE', { minimumFractionDigits: 2 })} ₺
                {Math.abs((parseFloat(nakit) || 0) + (parseFloat(iban) || 0) - netTutar) > 0.01 && (
                  <span className="text-red-700 ml-1">
                    (net {netTutar.toFixed(0)} ₺ olmalı)
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Not (opsiyonel)
          </label>
          <input
            type="text"
            value={notlar}
            onChange={(e) => setNotlar(e.target.value)}
            className="input-base"
            placeholder="Örn: 5 Mayıs banka transferi"
          />
        </div>

        {hata && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
            {hata}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onKapat}
            className="btn-secondary flex-1"
            disabled={kaydediliyor}
          >
            İptal
          </button>
          <button
            type="submit"
            className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2"
            disabled={kaydediliyor}
          >
            <CheckCircle2 className="w-4 h-4" />
            {kaydediliyor ? 'Kaydediliyor...' : 'Ödendi Olarak İşaretle'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
